// Pure gate-decision helpers for the Optum line-item appeal page.
//
// This module imports NOTHING from api/* or utils/axios — it is imported
// DIRECTLY by gateDecision.test.js so the unit test never transitively loads
// axios (appealbot-running-tests ESM gotcha). Keep it dependency-free.

/**
 * Decide whether the appeal page must hard-gate behind the ExistingReconGate
 * panel, given a /claims/{id}/claim-actions/eligibility/ response.
 *
 * Branch order (matches the backend precedence):
 *   1. reason === 'submitted' — every appealable line is already on a ticket
 *   2. reason === 'expects_update' — NOT_CREATABLE-failed or allowed_action='Update'
 *   3. otherwise no gate
 *
 * @param {object|null|undefined} eligibility
 * @returns {{gate: boolean, reason?: string, ticketId?: string, ticketReady?: boolean}}
 */
export function decideGate(eligibility) {
  const elig = eligibility || {};
  const ticketId = elig.existing_ticket_id;
  const existing = elig.existing_ticket || {};
  const allowedAction = (existing.allowed_action || '').trim().toLowerCase();

  if (elig.reason === 'submitted' && ticketId) {
    return { gate: true, reason: 'submitted', ticketId, ticketReady: true };
  }

  if (elig.reason === 'expects_update' && ticketId) {
    return {
      gate: true,
      reason: 'expects_update',
      ticketId,
      ticketReady: allowedAction === 'update' || allowedAction === 'view',
    };
  }

  return { gate: false };
}

/**
 * True iff a live submission failure is a NOT_CREATABLE rejection that should be
 * routed into the reconcile-then-update path instead of dead-ending on an Alert.
 *
 * @param {object|null|undefined} ticket
 * @returns {boolean}
 */
export function shouldReconcileOnFailure(ticket) {
  if (!ticket) return false;
  return ticket.status === 'submission_failed' && ticket.last_error_code === 'CLAIM_ACTION_NOT_CREATABLE';
}
